import { Box, Container, Drawer, Tab, Tabs } from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
// import { Chart } from "react-chartjs-2";

import { ExploreContext } from "../context/explore";
import styles from "../styles/components/explore-coords.module.scss";
// import { Select } from "./input";
// import { BarChart } from '@mui/x-charts/BarChart';
import classNames from "classnames";
import { format, getTime } from "date-fns";
import { AccountContext } from "../context/account";
import { MapDataContext } from "../context/map-data";
import { DataSourceModel, ProjectItem } from "../core/global";
import { isBrowser } from "../helpers/common";
import { isNumber } from "../helpers/core";
import { trackEvent } from "../helpers/tracking";
import { ExplorePlot, PlotType } from "./explore-plot";
import { ExploreStatisticsTable } from "./explore-statistics-table";
import Loader from "./loader";
import { ModalTitle } from "./modal-title";
import { ProjectItemDownloadButtons } from "./project-item-download";
import { ProjectItemOrderInfo } from "./project-item-order-info";

export const getProjectByLocation = (
  dataSourceModel: DataSourceModel | null,
  projects: ProjectItem[] | null,
  lat: number | null,
  lon: number | null
): ProjectItem | null => {
  if (isNumber(lat) && isNumber(lon) && dataSourceModel && projects && projects.length > 0) {
    const found = projects.filter((project) => {
      const { model, provider, geography, version, lat: _lat, lon: _lon } = project;
      return (
        model === dataSourceModel.model &&
        provider === dataSourceModel.provider &&
        geography === dataSourceModel.geography &&
        version === dataSourceModel.version &&
        _lat?.toFixed(5) === lat.toFixed(5) &&
        _lon?.toFixed(5) === lon.toFixed(5)
      );
    });
    if (found.length > 0) {
      return found[0];
    }
  }
  return null;
};

interface Plot {
  name: string;
  key: PlotType | null;
}
const plots: Plot[] = [
  {
    name: "Time Series",
    key: "timeseries",
  },
  {
    name: "Rose Plot",
    key: "rose",
  },
  {
    name: "Joint frequency distribution",
    key: "distribution",
  },
  {
    name: "Histogram",
    key: "histogram",
  },
  {
    name: "Statistics",
    key: "statistics",
  },
];


interface DataExplorationModuleProps {
  menuIsOpen: boolean;
}

export const DataExplorationModule = ({
  menuIsOpen,
}: DataExplorationModuleProps) => {
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [plotType, setPlotType] = useState<PlotType>("timeseries");
  const [tabIndex, setTabIndex] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    const newPlot = plots[newValue];
    const { key } = newPlot;
    setPlotType(key || "timeseries");
  };

  const { 
    setExploreLocationParams, 
    exploreLocationParams, 
    exploreDataSchema,
    isPurchasedPoint,
  } = useContext(ExploreContext);

  const {
    dataSources,
    dataSourceModel,
  } = useContext(MapDataContext);

  const { projects } = React.useContext(AccountContext);

  const { lat, lon, model } = useMemo(() => {
    if (exploreDataSchema !== null) {
      const {
        description: { model, lat, lon },
      } = exploreDataSchema;
      return { lat, lon, model };
    }
    return {}
  }, [JSON.stringify(exploreDataSchema)]);

  const setToClose = useCallback((_open: boolean) => {
    window.setTimeout(() => {
      setExploreLocationParams(null);
      setPlotType("timeseries");
      setTabIndex(0);
    }, 100);
    setDrawerIsOpen(_open);
  }, []);

  useEffect(() => {
    setPlotType("timeseries");
    setTabIndex(0);
    setDrawerIsOpen(false);
    if (lat || lon) {
      trackEvent("explore-modal", {lat, lon, model})
    }
  }, []);

  useEffect(() => {
    if (!menuIsOpen) {
      setToClose(false);
    }
  }, [menuIsOpen]);

  useEffect(() => {
    if (exploreLocationParams === null) {
      setToClose(false);
    }
  }, [exploreLocationParams]);

  const onClose = () => {
    setToClose(false);
  };

  const currentProjectPoint: ProjectItem | null = useMemo(() => {
    if (dataSourceModel !== null && projects !== null && isNumber(lat) && isNumber(lon)) {
      return getProjectByLocation(dataSourceModel, projects, lat, lon);
    }
    return null;
  }, [JSON.stringify(dataSourceModel), JSON.stringify(projects), model, lat, lon]);


  useEffect(() => {
    if (currentProjectPoint && exploreDataSchema) {

      const startDate = getTime(new Date(currentProjectPoint.start_date))
      const endDate = getTime(new Date(currentProjectPoint.end_date))
      if (exploreDataSchema?.timeline.startDate !== startDate || exploreDataSchema?.timeline.endDate !== endDate) {        
        const { model, provider, geography, lat, lon, path, version } = currentProjectPoint;
        const [,orderId, filename] = (path || '').split("/");
        setExploreLocationParams({
          model, 
          provider,
          geography,
          version,
          lat, 
          lon, 
          startDate, 
          endDate, 
          orderId,
          filename,
        });
      }
    }
  }, [JSON.stringify(currentProjectPoint), JSON.stringify(exploreDataSchema)])
  

  useEffect(() => {
    if (
      exploreLocationParams !== null
    ) {
      setDrawerIsOpen(true);
    }
  }, [exploreLocationParams]);

  if (!isBrowser()) {
    return null;
  }

  return (
    <Box
      id="data-exploration-module"
      className={classNames(styles.drawerWrapper, {
        [styles.expanded]: drawerIsOpen,
      })}
    >
      <Drawer variant="permanent" open={drawerIsOpen} className={styles.drawer}>
        <ModalTitle id="data-exploration-module-title" inlineTitle onClose={onClose}>
          Data Visualisation Module{(model && dataSources && Object.prototype.hasOwnProperty.call(dataSources, model)) ? `: ${dataSources[model].name}` : ``}
        </ModalTitle>
        <Container
          maxWidth={false}
          sx={{ m: 0, height: "100%" }}
        >
          {currentProjectPoint && (
            <div>
              You have data from <strong>{format(new Date(currentProjectPoint.start_date), 'MMM yyyy')}</strong> to <strong>{format(new Date(currentProjectPoint.end_date), 'MMM yyyy')}</strong>{" "}
                 for this location: {" "}
              <strong>
                {currentProjectPoint.name ||
                  `${currentProjectPoint.lat.toFixed(
                    5
                  )}, ${currentProjectPoint.lon.toFixed(5)}`}
              </strong>
              {currentProjectPoint.description && (
                <>
                  Description:{" "}
                  <strong>{currentProjectPoint.description}</strong>
                </>
              )}
            </div>
          )}
          <div style={{position: "relative"}}>
          <Tabs
            id="explore-plot-tabs"
            className={styles.tabs}
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {(isPurchasedPoint? plots : plots).map((plot) => {
              const { key, name } = plot;
              return <Tab key={key} label={name} />;
            })}
          </Tabs>
            {currentProjectPoint && <div className={styles.downloadButtons}><ProjectItemDownloadButtons item={currentProjectPoint} /><ProjectItemOrderInfo orderId={currentProjectPoint.order_id} /></div>}
          </div>
          {drawerIsOpen ? (
            <>
              {plotType === "statistics" ? (
                <ExploreStatisticsTable
                  exploreDataSchema={exploreDataSchema}
                />
              ) : (
                <ExplorePlot type={plotType} />
              )}
            </>
          ) : (
            <div style={{ width: "100%", padding: "240px 0 64px 0" }}>
              <Loader center showCog />
            </div>
          )}
        </Container>
      </Drawer>
    </Box>
  );
};

export default DataExplorationModule;